// Local, rule-based SEO analysis for the blog editor. Runs entirely in the browser — no AI, no credits.
const stripTags = (html = "") => html.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ");

export const plainText = (post) => {
    const parts = [];
    (post.blocks || []).forEach((b) => {
        if (["text", "tip", "info", "warning", "quote", "cta"].includes(b.type)) parts.push(stripTags(b.html || b.text || ""));
        else if (b.type === "heading") parts.push(b.text || "");
        else if (b.type === "faq") (b.items || []).forEach((i) => parts.push(`${i.question || ""} ${i.answer || ""}`));
        else if (b.type === "table") (b.rows || []).forEach((r) => parts.push(r.join(" ")));
    });
    return parts.join(" ").replace(/\s+/g, " ").trim();
};

export const wordCount = (post) => plainText(post).split(" ").filter(Boolean).length;
export const readingMinutes = (post) => Math.max(1, Math.round(wordCount(post) / 220));

const hasKw = (text = "", kw = "") => !!kw && text.toLowerCase().includes(kw.toLowerCase().trim());

export function analyse(post = {}) {
    const seo = post.seo || {};
    const kw = (seo.focus_keyword || "").trim();
    const title = (post.title || "").trim();
    const seoTitle = (seo.seo_title || title).trim();
    const meta = (seo.meta_description || "").trim();
    const slug = (post.slug || "").trim();
    const blocks = post.blocks || [];
    const text = plainText(post);
    const words = wordCount(post);
    const headings = blocks.filter((b) => b.type === "heading");
    const h2 = headings.filter((b) => String(b.level || 2) === "2");
    const images = blocks.filter((b) => b.type === "image" || b.type === "gallery");
    const hrefs = blocks
        .filter((b) => ["text", "tip", "info", "warning"].includes(b.type))
        .flatMap((b) => [...(b.html || "").matchAll(/href="([^"]+)"/g)].map((m) => m[1]));
    const ctaHrefs = blocks.filter((b) => b.type === "cta" && b.href).map((b) => b.href);
    const productBlocks = blocks.filter((b) => b.type === "product" || b.type === "consultation");
    const internal = [...hrefs, ...ctaHrefs].filter((h) => h.startsWith("/"));
    const external = hrefs.filter((h) => h.startsWith("http"));

    const checks = [];
    const add = (id, group, label, ok, warnMsg, goodMsg, hard = false) =>
        checks.push({ id, group, label, status: ok ? "good" : hard ? "bad" : "warn", message: ok ? goodMsg : warnMsg });

    add("title_exists", "Title", "Title added", !!title, "Give the article a title.", "Title is in place.", true);
    add("title_length", "Title", "Title length 30–65 characters", seoTitle.length >= 30 && seoTitle.length <= 65,
        `Title is ${seoTitle.length} characters — aim for 30 to 65 so it is not cut off in search results.`,
        `Good length (${seoTitle.length} characters).`);
    if (kw) {
        add("title_keyword", "Title", "Keyword in title", hasKw(seoTitle, kw),
            `Consider using “${kw}” naturally in the title.`, "Keyword appears in the title.");
        const at = seoTitle.toLowerCase().indexOf(kw.toLowerCase());
        add("title_keyword_early", "Title", "Keyword near the start", at >= 0 && at < 40,
            "Moving the keyword closer to the start helps readers scan the result.", "Keyword appears early in the title.");
    }

    add("meta_exists", "Meta description", "Meta description added", !!meta,
        "Write a short description for search results.", "Meta description is in place.");
    add("meta_length", "Meta description", "120–160 characters", meta.length >= 120 && meta.length <= 160,
        `Meta description is ${meta.length} characters — 120 to 160 reads best in Google.`,
        `Good length (${meta.length} characters).`);
    if (kw) add("meta_keyword", "Meta description", "Keyword in description", hasKw(meta, kw),
        `Try including “${kw}” once in the description.`, "Keyword appears in the description.");

    add("slug_exists", "URL", "URL added", !!slug, "The article needs a web address.", "URL is set.", true);
    add("slug_length", "URL", "URL is short", slug.length > 0 && slug.length <= 60,
        "Shorten the web address — under 60 characters works best.", "URL length is fine.");
    add("slug_clean", "URL", "No odd characters", /^[a-z0-9-]*$/.test(slug),
        "Use lowercase letters, numbers and hyphens only.", "URL is clean.");
    if (kw) add("slug_keyword", "URL", "Keyword in URL", hasKw(slug.replace(/-/g, " "), kw),
        `Consider putting “${kw}” in the web address.`, "Keyword appears in the URL.");

    add("content_length", "Content", "At least 300 words", words >= 300,
        `The article has ${words} words — around 600 or more reads as a complete guide.`, `${words} words of content.`);
    if (kw) {
        const density = (text.toLowerCase().split(kw.toLowerCase()).length - 1);
        add("content_keyword", "Content", "Keyword used in the body", density >= 1,
            `Use “${kw}” naturally somewhere in the article.`,
            `Keyword used ${density} time${density === 1 ? "" : "s"}.`);
        add("keyword_stuffing", "Content", "No keyword stuffing", words === 0 || (density / Math.max(words, 1)) * 100 <= 2.5,
            "The keyword appears too often — write for the reader instead.", "Keyword use looks natural.");
        add("heading_keyword", "Content", "Keyword in one heading", headings.some((b) => hasKw(b.text || "", kw)),
            `Consider using “${kw}” naturally in one H2 heading.`, "Keyword appears in a heading.");
    }
    add("headings_exist", "Headings", "Uses H2 sections", h2.length >= 2,
        "Break the article into at least two H2 sections so it is easy to scan.", `${h2.length} H2 sections.`);
    add("heading_order", "Headings", "Heading order is correct",
        !(headings.length && String(headings[0].level || 2) === "3"),
        "An H3 appears before any H2 — start each part with an H2.", "Heading order looks right.");
    add("paragraphs", "Content", "Readable paragraphs",
        blocks.filter((b) => b.type === "text").every((b) => stripTags(b.html || "").split(" ").length < 180),
        "One paragraph block is very long — split it for easier reading.", "Paragraph lengths are comfortable.");
    add("internal_links", "Links", "Links to your own pages", internal.length + productBlocks.length >= 1,
        "Add a link, product card or consultation CTA pointing into ReHealthMe.",
        `${internal.length + productBlocks.length} internal link(s).`);
    add("external_links", "Links", "Reference or source link", external.length >= 1,
        "A reference link adds trust to health content.", `${external.length} external link(s).`);

    add("featured_image", "Images", "Featured image added", !!(post.featured_image || {}).url,
        "Add a featured image — it is used on the blog list and in social shares.", "Featured image is in place.");
    add("featured_alt", "Images", "Featured image ALT text", !!(post.featured_image || {}).alt,
        "Describe the featured image so search engines and screen readers understand it.", "Featured ALT text added.");
    add("body_images", "Images", "Images inside the article", images.length >= 1,
        "Add at least one image inside the article to break up the text.", `${images.length} image block(s).`);
    const missingAlt = blocks.filter((b) => b.type === "image" && !(b.alt || "").trim());
    add("image_alt", "Images", "Every image has ALT text", missingAlt.length === 0,
        `${missingAlt.length} image(s) have no ALT text.`, "All images have ALT text.");

    add("category", "Setup", "Category selected", !!post.category_id,
        "Choose a category so the article is grouped correctly.", "Category selected.");
    add("author", "Setup", "Author selected", !!post.author_id, "Choose who published the article.", "Author selected.");
    add("excerpt", "Setup", "Short excerpt added", !!(post.excerpt || "").trim(),
        "Write a one-line summary shown on the blog list.", "Excerpt added.");

    const good = checks.filter((c) => c.status === "good").length;
    const score = checks.length ? Math.round((good / checks.length) * 100) : 0;
    return { score, checks, words, reading_minutes: readingMinutes(post), grade: score >= 80 ? "good" : score >= 55 ? "ok" : "poor" };
}

// Live title-only feedback while the admin types
export function titleChecks(title = "", kw = "") {
    const t = title.trim();
    const out = [
        { label: "Title added", status: t.length > 0 ? "good" : "bad" },
        { label: `Length ${t.length}/65`, status: t.length >= 30 && t.length <= 65 ? "good" : "warn" },
        { label: "Clear and readable", status: t.split(" ").filter(Boolean).length >= 4 ? "good" : "warn" },
    ];
    if (kw) out.push({ label: `Contains “${kw}”`, status: hasKw(t, kw) ? "good" : "warn" });
    return out;
}

export const slugify = (value = "") =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");
